<template>
	<div>
		<BaseFileUploader
			@input="onLoadFile"
			ref="upload"
			:title="$t('upload')"
			:accept="accept"
			:handle-on-click="handleOnClick"
			:multiple="multiple"
			:maxfiles="maxfiles"
			:allow-upload-in-app-container="allowUploadInAppContainer"
			@update:filename="
				uploadFileName = $event;
				$emit('update:filename', uploadFileName);
			"
			v-if="!isAppContainer"
		>
			<slot :did-upload="didUpload">
				<div class="h-5 text-xs flex items-center text-white py-2 px-1 bg-connectProBlue">
					<font-awesome-icon :icon="['fas', 'upload']"></font-awesome-icon>
				</div>
			</slot>
		</BaseFileUploader>
		<BaseCamera
			ref="camera"
			@snapshot="onLoadFile($event)"
			:disable-success-message="true"
			:close-on-snapshot="false"
			:take-image="true"
			:label="$t('add_document')"
			:take-image-label="$t('scan_document')"
			:allow-upload-in-app-container="allowUploadInAppContainer"
			:remove-upload-button="true"
			:enable-preview="false"
			:ask-for-overwrite="isDocumentUuid"
      :allow-multiple-photos="allowMultiplePhotos"
			v-else-if="isAppContainer"
		>
			<template #takeImage>
				<slot :did-upload="didUpload">
					<div class="h-5 text-xs flex items-center text-white py-2 px-1 bg-connectProBlue">
						<font-awesome-icon :icon="['fas', 'upload']" />
					</div>
				</slot>
			</template>
		</BaseCamera>
	</div>
</template>
<script>
import BaseFileUploader from '@/components/forms/inputs/BaseFileUploader';
import { DamagecasesApiFactory } from '@/api/generated';
import defaultConfiguration from '@/api/defaultConfiguration';
import LoadingSpinner from '@/mixins/LoadingSpinner';
import BaseCamera from '@/components/forms/inputs/BaseCamera';
import AppContainerMixin from '@/mixins/AppContainerMixin';
import { getStringFromDate, getTimeFromDate } from '@/plugins/Utils';

export default {
	name: 'DocumentFileUploader',
	mixins: [LoadingSpinner, AppContainerMixin],
	components: { BaseCamera, BaseFileUploader },
	props: {
		document: {},
		instantUpload: {
			default: true,
		},
		handleOnClick: { default: true },
		accept: { default: 'image/png, image/jpeg, application/pdf' },
		damageCaseId: {},
		multiple: { default: false },
		maxfiles: { default: false },
		allowUploadInAppContainer: { default: false },
		overwriteUuid: { default: null },
		repairExtensionId: { required: false },
    allowMultiplePhotos: {default: false}
	},
	data() {
		return {
			didUpload: false,
			uploadFileName: null,
			base64Content: null,
			miscOverride: false,
		};
	},
	computed: {
		isDocumentUuid() {
			return this.overwriteUuid !== null;
		},
	},
	methods: {
		openCamera() {
			this.$refs.camera.openCamera();
		},
		openUpload() {
			this.$refs.upload.openUpload();
		},
		onLoadFile($event) {
			if (this.isAppContainer && !this.allowUploadInAppContainer) {
				this.uploadFileName = `Aufnahme ${getStringFromDate(new Date())} - ${getTimeFromDate(new Date())}`;
			}
			this.$emit('update:filename', this.uploadFileName);
			if (!this.isAppContainer) {
				this.base64Content = $event;
			}

			this.$emit('input', $event);
			if (this.instantUpload && !this.isAppContainer) {
				this.uploadFile($event);
			}
		},
		triggerFileUpload(customUploadFileName = null, miscOverride = false) {
			if (!miscOverride) {
				this.overwriteId = 0;
			}
			if (this.base64Content) {
				if (customUploadFileName) {
					this.uploadFileName = customUploadFileName;
				}
				return this.uploadFile(this.base64Content);
			} else {
				// eslint-disable-next-line
				console.warn('tried to upload a document without content');
				return false;
			}
		},
		uploadFile($event) {
			this.setLoadingStatus(true);
			const damagecasesApiFactory = DamagecasesApiFactory(defaultConfiguration.config(), defaultConfiguration.basePath, defaultConfiguration.axios);
			let promise = '';
			if (this.document.type === 'misc' || this.document.type === 'damageassessor_documents') {
				promise = damagecasesApiFactory.pdfDamagecasesIdTypeUploadLabelPost(
					this.damageCaseId || this.$route.params.processId || this.$route.params.id,
					this.document.type || this.document.document_type,
					this.uploadFileName,
					this.overwriteUuid ? this.overwriteUuid : undefined,
					$event,
				);
			} else if (this.document.type === 'reparaturerweiterung_pdf' || this.document.type === 'reparaturerweiterung_zip') {
				promise = damagecasesApiFactory.pdfDamagecasesIdTypeUploadRepairExtensionRepairExtensionIdPost(
					this.damageCaseId || this.$route.params.processId || this.$route.params.id,
					this.document.type || this.document.document_type,
					this.repairExtensionId.toString(),
					$event,
				);
			} else {
				promise = damagecasesApiFactory.pdfDamagecasesIdTypeUploadPost(
					this.damageCaseId || this.$route.params.processId || this.$route.params.id,
					this.document.type || this.document.document_type,
					$event,
				);
			}

			return promise
				.then(response => {
					this.setLoadingStatus(false);
					if (this.instantUpload) {
						this.$swal(this.$t('general.success').toString(), this.$t('file_uploaded').toString(), 'success');
					}
					this.base64Content = null;
					this.$store.dispatch('updateDocuments');
					this.didUpload = true;
					this.$emit('update:document', response.data);
					this.$store.commit('setUploadNew', true);
				})
				.catch(error => {
					this.setLoadingStatus(false);
					if (this.instantUpload) {
						this.$swal(this.$t('general.error').toString(), this.$t('file_not_uploaded').toString(), 'error');
					} else {
						throw error;
					}
				});
		},
	},
};
</script>
